import React, { Suspense } from "react";
import { useEffect, useMemo, useRef } from "react";
import type {
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
} from "next";
import { useRouter } from "next/router";
import { BreadcrumbJsonLd } from "next-seo";
import { get } from "lodash";
import { useQuery } from "@apollo/client";

import SEOTags from "~/components/SEOTags";
import { Identifiers, PATH, SectionsKey } from "~/constants/enum";
import { QUERY_GET_COLLECTIONS } from "~/data/collections";
import { GET_SCHEMA_COLLECTION } from "~/data/schemas/schema.gql";
import { useGetFilterValues } from "~/hooks/useGetFilterValues";
import Layout from "~/layouts/ThemeLayout";
import CollectionSchema from "~/schema/CollectionSchema";
import { ProductListProps } from "~/sections/ProductList/ProductList";
import { Product } from "~/services/product";
import { trackingCategory } from "~/services/tracking";
import { initializeSSRApollo } from "~/utils/apollo-client";
import {
  converShortCodeFilterKey,
  convertCleanFilterKey,
} from "~/utils/converters";
import { createSortObject } from "~/utils/filter";
import {
  getPageStruct,
  renderSectionByKey,
  renderSectionNew,
} from "~/utils/pageTwo";
import { getTheme } from "~/utils/theme";
import { getUrlByTheme, getUrlCustom } from "~/utils/theme";
import { transformProductItems } from "~/utils/transform";
import { QUERY_GET_MEGA_MENU_GQL } from "~/data/mega-menu/gql";
import {
  GET_STORE_CONFIG_ON_DOCUMENT,
  QUERY_GET_ALL_REVIEWS_ONLY_IMG,
  QUERY_GET_CMS_BLOCKS,
} from "~/data/home";
import { GET_PRODUCTS_TRACKING } from "~/data/product/gql";

const SSRIdentifiers = [Identifiers.BenefitsSection, Identifiers.FollowUs];

const pageStruct = getPageStruct(getTheme(), "collection-detail");

const sections: any = pageStruct?.children
  ?.sort((a: any, b: any) => a.order - b.order)
  .map((item: any, index: number) => {
    const Section = renderSectionByKey(item.id);

    return {
      id: item.id,
      order: item.order,
      dataKey: item.dataKey,
      identifier: item.identifier,
      component: Section,
    };
  });
const CollectionDetailPage = (
  props: InferGetServerSidePropsType<typeof getServerSideProps>
) => {
  const {
    slug,
    configSchema,
    sortFields,
    filterData,
    dataMegaMenu,
    storeConfig,
    siteReviews,
    serverCmsBlocks,
  } = props;

  const router = useRouter();
  const { query } = router;

  //Filter Values
  const {
    filterValues,
    pageSize = 30,
    currentPage = 1,
    sort,
  } = useGetFilterValues();
  const pagination = {
    pageSize: pageSize,
    currentPage,
  };
  //Products
  const { data: products, loading } = useQuery(QUERY_GET_COLLECTIONS, {
    variables: {
      page_size: pagination.pageSize,
      current_page: pagination.currentPage,
      url_key: slug,
      filter: convertCleanFilterKey(
        filterValues,
        filterData?.map((i: any) => i?.attribute_code) || []
      ),
      sort: sort ?? { relevance: "DESC" },
    },
    // nextFetchPolicy: "network-only",
    context: { fetchOptions: { method: "GET" } },
    skip: !slug,
  });

  const { data: dataCollectionTracking, loading: loadingCollectionTracking } =
    useQuery(GET_PRODUCTS_TRACKING, {
      variables: {
        skus:
          (products &&
            products?.productCollection?.[0]?.items?.map(
              (product: Product) => product.sku + ""
            )) ||
          [],
      },
      context: { fetchOptions: { method: "GET" } },
      skip: !products || !products?.productCollection?.[0]?.items?.length,
    });

  const productCollection: Product[] = useMemo(() => {
    if (!!loading || !products)
      // return Array.from({ length: pageSize }).map(
      //   (_, index) => ({ id: index.toString() } as Partial<Product>)
      // );
      return [];

    if (loadingCollectionTracking || !dataCollectionTracking) {
      return transformProductItems(
        get(products, "productCollection[0].items", [])
      );
    }

    return transformProductItems(
      get(
        {
          ...products,
          productCollection: products?.productCollection?.map(
            (product: any) => ({
              ...product,
              items: product.items.map((item: Product) => {
                const tracking =
                  dataCollectionTracking?.productsBySkus?.items?.find(
                    (trackItem: any) => trackItem.sku === item.sku
                  );
                return { ...item, ...tracking };
              }),
            })
          ),
        },
        "productCollection[0].items",
        []
      )
    );
  }, [products, loading, loadingCollectionTracking, dataCollectionTracking]);

  const initTrackingcategory = useRef(false);
  useEffect(() => {
    if (!!initTrackingcategory.current) return;
    trackingCategory({
      keyword: slug,
      data: productCollection as Product[],
    });
    initTrackingcategory.current = true;
  }, [slug, productCollection]);

  const nameCollection = useMemo(() => {
    return get(products, "productCollection[0].name", slug) || slug;
  }, [products, slug]);

  const total = useMemo(
    () => get(products, "productCollection[0].total_count", 0),
    [products]
  );
  const description = useMemo(
    () => get(products, "productCollection[0].description", ""),
    [products]
  );
  const campaign_id = useMemo(
    () => get(products, "productCollection[0].campaign_id", null),
    [products]
  );
  const is_artist = useMemo(
    () => get(products, "productCollection[0].is_artist", null),
    [products]
  );
  const artist_thumbnail = useMemo(
    () => get(products, "productCollection[0].artist_thumbnail", null),
    [products]
  );

  const sectionData = useMemo(() => {
    return {
      [SectionsKey.ProductList]: {
        breadcrumbs: [
          { title: "Home", path: "/" },
          { title: "Collections", path: PATH.Collections },
          { title: nameCollection },
        ],
        title: nameCollection,
        data: productCollection,
        filters: {
          aggregations: filterData,
          sortFields: sortFields,
        },
        isShowFilter: query?.filter === "show",
        total,
        isLoading: loading,
        defaultSort: "relevance-DESC",
        identifier: "collection",
        listKey: campaign_id,
        is_artist,
        artist_thumbnail,
        description,
      } as ProductListProps,
      [SectionsKey.CollectionsContent]: {
        data: description,
      },
      [SectionsKey.HomePageReviews]: {
        siteReviews: siteReviews,
      },
      [SectionsKey.BenefitsSection]: {
        data: serverCmsBlocks?.[Identifiers.BenefitsSection],
      },
      [SectionsKey.FollowUs]: {
        data: serverCmsBlocks?.[Identifiers.FollowUs],
      },
    };
  }, [
    campaign_id,
    filterData,
    productCollection,
    total,
    sortFields,
    loading,
    description,
  ]);

  const { meta_title, meta_description, url_key, name } =
    props?.metaCollections;
  const dataCarouselJsonLd: any[] =
    !!props.metaCollections?.items?.length &&
    props.metaCollections?.items?.map(
      ({ url_key, name, thumbnail, price_range }: any) => {
        return {
          keywords: url_key,
          thumbnailUrl: thumbnail?.url,
          image: thumbnail?.url,
          name,
          about: name,
          availability: "instock",
          url: getUrlByTheme(PATH.ProductDetail.replace("[slug]", url_key)),
          offers: {
            price: price_range?.minimum_price?.final_price?.value,
            priceCurrency: price_range?.minimum_price?.final_price?.currency,
            url: getUrlByTheme(PATH.ProductDetail.replace("[slug]", url_key)),
          },
        };
      }
    );
  const itemListElements = [
    {
      position: 1,
      name: "Home",
      item: process.env.NEXT_PUBLIC_URL + "",
    },
    {
      position: 2,
      name: meta_title,
      item: getUrlByTheme(`/collections/${slug}`),
    },
  ];

  return (
    <>
      <SEOTags
        title={meta_title || name}
        description={meta_description || name}
        keywords={url_key}
        url={getUrlCustom({
          pathname: PATH.CollectionDetail,
          query: {
            slug: props?.metaCollections?.url_key,
          },
        })}
        type="product.group"
      >
        <link
          rel="canonical"
          href={getUrlCustom({
            pathname: process.env.NEXT_PUBLIC_URL + PATH.CollectionDetail,
            query: {
              slug: props?.metaCollections?.url_key,
            },
          })}
          data-vm-ssr="true"
        />
      </SEOTags>
      <CollectionSchema
        config={configSchema}
        dataMeta={{
          name: meta_title || name,
          description: meta_description || name,
          isRelatedTo: dataCarouselJsonLd || [],
        }}
      />

      {!!itemListElements.length && (
        <BreadcrumbJsonLd itemListElements={itemListElements} />
      )}

      <Layout dataMegaMenu={dataMegaMenu} storeConfig={storeConfig}>
        <div className="flex flex-col gap-8">
          {sections?.map((item: any, index: number) => {
            const Section = item.component;

            return (
              <React.Fragment key={`section-${index}`}>
                {Section ? (
                  <Section
                    data={serverCmsBlocks?.[item.identifier]}
                    {...get(sectionData, item.dataKey, {})}
                  />
                ) : null}
              </React.Fragment>
            );
          })}
        </div>
      </Layout>
    </>
  );
};

export default CollectionDetailPage;

export async function getServerSideProps({
  params,
  query,
}: GetServerSidePropsContext) {
  const apolloClient = initializeSSRApollo({
    httpLinkProps: { useGETForQueries: true },
  });

  try {
    const filter = converShortCodeFilterKey(query as any, "decode");

    const [
      dataSchemaRes,
      productsRes,
      dataMegaMenuRes,
      responseRes,
      siteReviewsRes,
      blockDataRes,
    ] = await Promise.allSettled([
      apolloClient.query({
        query: GET_SCHEMA_COLLECTION,
      }),
      apolloClient.query({
        query: QUERY_GET_COLLECTIONS,
        variables: {
          url_key: params?.slug as string,
          page_size: query?.pageSize || 30,
          current_page: query?.currentPage || 1,
          filter: convertCleanFilterKey(filter, {}) || {},
          sort: createSortObject(query.sort as string, "-") || {
            relevance: "DESC",
          },
        },
      }),
      apolloClient.query({
        query: QUERY_GET_MEGA_MENU_GQL,
        variables: {
          identifiers: Identifiers.MainMenu,
        },
        context: { fetchOptions: { method: "GET" } },
      }),
      apolloClient.query({
        query: GET_STORE_CONFIG_ON_DOCUMENT,
      }),
      apolloClient.query({
        query: QUERY_GET_ALL_REVIEWS_ONLY_IMG,
        variables: {
          images: true,
          pageSize: 4,
        },
        context: { fetchOptions: { method: "GET" } },
      }),
      apolloClient.query({
        query: QUERY_GET_CMS_BLOCKS,
        variables: { identifiers: SSRIdentifiers },
        context: { fetchOptions: { method: "GET" } },
      }),
    ]);

    const dataSchema = (dataSchemaRes as any)?.value?.data;
    const products = (productsRes as any)?.value?.data;
    const dataMegaMenu = (dataMegaMenuRes as any)?.value?.data;
    const response = (responseRes as any)?.value;
    const siteReviews = (siteReviewsRes as any)?.value?.data;
    const blockData = (blockDataRes as any)?.value?.data;

    const metaData = {
      metaCollections: {
        meta_title: "",
        meta_description: "",
        url_key: "",
        name: "",
      } as any,
    };

    const serverCmsBlocks = blockData.cmsBlocks.items.reduce(
      (acc: any, cur: any) => {
        acc[cur.identifier] = cur;
        return acc;
      },
      {} as any
    );

    metaData.metaCollections = get(products, "productCollection[0]", {});
    const isArtist = get(products, "productCollection[0].is_artist", null);
    if (isArtist) {
      const artistUrlKey = get(products, "productCollection[0].url_key", null);
      return {
        redirect: {
          destination: `/artists/${artistUrlKey || params?.slug}`,
          permanent: false,
        },
      };
    }
    const sortFields = get(products, "productCollection[0].sort_fields", []);
    const filterData = get(products, "productCollection[0].aggregations", []);
    const storeConfig = get(response, "data.storeConfig", {
      frontend_config_logo: null,
      store_notice_enable: null,
    });

    return {
      props: {
        slug: params?.slug as string,
        configSchema: dataSchema?.MarketingConfig?.schemaCollection,
        sortFields,
        filterData,
        initialApolloState: apolloClient.cache.extract(),
        dataMegaMenu,
        storeConfig,
        siteReviews: siteReviews ?? null,
        serverCmsBlocks,
        ...metaData,
      },
    };
  } catch (error) {
    console.log(error);

    return {
      notFound: true,
    };
  }
}
